import moment from 'moment';
import ErrorMessages from '../../../const/errorMessages';
import { getTranslation as translate } from '../../../../utils/';
import { getFileSize } from '../../../../utils/upload';

const ImageRegExp = /^<img.*\/>/;

const DocTypes = {
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  xls: 'excel',
  xlsx: 'excel',
};

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseLinks(text) {
  let message = text;
  const links = message.match(/((https:\/\/(www|\S+)|http:\/\/(www|\S+)|www)(\.\S+)+)/g);
  if (!ImageRegExp.test(text) && links) {
    const replacedLinks = [];
    links.forEach((link) => {
      let protocol = '';
      if (!link.match(/^http/)) {
        protocol = '//';
      }
      if (replacedLinks.indexOf(link) === -1) {
        message = message.replace(
          new RegExp(escapeRegExp(link), 'g'),
          `<a href="${protocol}${link}" target="_blank">${link}</a>`
        );
        replacedLinks.push(link);
      }
    });
  }
  return message;
}

const Parser = {};

Parser.agentMessage = data => ({
  type: 'agent',
  body: {
    contentType: ImageRegExp.test(data.body) ? 'single-image' : '',
    text: parseLinks(data.body).replace(/\n/g, '<br />'),
    side: 'left',
    animate: true,
  },
  timestamp: data.head.timestamp,
});

Parser.customerMessage = (msg = '', msgData = {}) => {
  if (msgData.cmd === 'FILE') {
    const { body: downloadUrl, head: { fileType, fileName, fileSize } } = msgData;
    return Parser.customerFile({
      name: fileName,
      fileType,
      fileName,
      fileSize,
      downloadUrl,
      size: getFileSize(fileSize),
      hideProgress: true,
    });
  }
  return {
    type: 'consumer',
    body: {
      // userId: 1,
      contentType: ImageRegExp.test(msg) ? 'single-image' : '',
      text: parseLinks(msg).replace(/\n/g, '<br />'),
      side: 'right',
      animate: true,
    },
    timestamp: moment().valueOf(),
  };
};

Parser.customerImage = url => ({
  type: 'consumer',
  body: {
    contentType: 'single-image',
    text: `<img src="${url}" />`,
    side: 'right',
    animate: true,
  },
  timestamp: moment().valueOf(),
});

Parser.customerFile = fileInfo => ({
  type: 'fileTransferCustomer',
  body: {
    ...fileInfo,
    type: DocTypes[fileInfo.fileType],
    contentType: 'file',
    side: 'right',
    animate: true,
  },
  timestamp: moment().valueOf(),
});

Parser.errorMessage = (data) => {
  const { code, customCode } = data;
  const errorMsg = ErrorMessages[code] || ErrorMessages[customCode] || '';

  return {
    body: {
      text: translate(errorMsg).replace(/\n/g, '<br />'),
    },
  };
};

Parser.agentJoin = data => ({
  body: {
    text: translate('csc.agent_assigned', {
      agentName: data.head.name,
    }),
  },
});

Parser.agentLeft = (data) => {
  const leftTypes = {
    0: 'csc.agent_left',
    1: 'csc.customer_login_other_device',
    2: 'csc.agent_left',
    3: 'csc.agent_transfer',
    5: 'csc.session_timeout',
    byUser: 'csc.user_left',
  };
  const headData = data.data.head;
  const leftInfo = `${headData.closeReasonType}.${headData.closeReasonDetail}`;
  let msg = '';

  switch (leftInfo) {
    case '2.10':
      msg = leftTypes.byUser;
      break;
    case '1.0':
    case '1.1':
    case '1.6':
      msg = leftTypes['5'];
      break;
    default:
      msg = leftTypes[data.type] || leftTypes[0];
  }

  return {
    body: {
      text: translate(msg, {
        agentName: data.data.head.name,
      }),
    },
  };
};

Parser.agentTransfer = () => ({
  body: {
    text: translate('csc.agent_transfer'),
  },
});

Parser.historyMessage = (message) => {
  if (message.head.senderType === '1') {
    return Parser.customerMessage(message.body, message);
  }
  return Parser.agentMessage(message);
};

Parser.queuing = queue => ({
  body: {
    text: translate('csc.queue_for_agent', { queue }),
  },
});

Parser.transferAgent = () => ({
  body: {
    text: translate('csc.transfering_to_agent'),
  },
});

Parser.resumeSuccessful = () => ({
  body: {
    text: translate('csc.resume_successful'),
  },
});

Parser.queueNotice = () => ({
  body: {
    text: translate('csc.non_office_hour_queue_notice'),
  },
});

Parser.timestamp = t => ({
  body: {
    text: moment(t).format('HH:mm'),
  },
});

Parser.feedback = (data) => {
  const questions = data.questions;
  const logicQuestions = {};
  let ratingQuestion = null;
  let ratingOption = null;

  questions.forEach((question) => {
    if (question.type === 1) {
      data.comment = question;
    }
    if (question.logic) {
      logicQuestions[question.logic.optionId] = question;
      data.hasLogicQuestion = true;
    }
  });

  if (questions[0]) {
    ratingQuestion = questions[0];
    ratingOption = ratingQuestion.options;

    ratingOption.sort((a, b) => {
      if (a.code < b.code) return -1;
      return 1;
    });
    ratingQuestion.options = [ratingOption[0]]; // min score
    if (ratingOption.length > 1) {
      ratingQuestion.options.push(ratingOption.pop()); // max score
      ratingQuestion.options[1].type = 'great';
    }
    ratingQuestion.options[0].type = 'bad';

    if (data.hasLogicQuestion) {
      ratingQuestion.options.forEach((option) => {
        option.reasons = logicQuestions[option.id];
      });
    } else {
      ratingQuestion.options.forEach((option) => {
        option.reasons = questions[1] || {};
      });
    }

    data.rating = ratingQuestion;
  }

  delete data.questions;
  return data;
};

Parser.submitFeedback = (answer, questions) => {
  const data = [];
  const ratingAnswer = {
    questionId: questions.rating.id,
    questionType: questions.rating.type,
    answer: [
      {
        optionId: answer.rating.id,
      },
    ],
  };
  let reasonQuestion = null;
  let reasonAnswer = null;
  let commentAnswer = null;

  questions.rating.options.forEach((option) => {
    if (option.id === answer.rating.id) {
      reasonQuestion = option;
    }
  });

  if (reasonQuestion) {
    reasonAnswer = {
      questionId: reasonQuestion.reasons.id,
      questionType: reasonQuestion.reasons.type,
      answer: answer.rating.reasons.map(option => ({ optionId: option.id })),
    };
  }

  if (questions.comment) {
    commentAnswer = {
      questionId: questions.comment.id,
      questionType: questions.comment.type,
      answer: [
        {
          text: answer.comment || '',
        },
      ],
    };
  }

  data.push(ratingAnswer);
  data.push(reasonAnswer);
  data.push(commentAnswer);

  return { answers: JSON.stringify(data) };
};

export default Parser;



// WEBPACK FOOTER //
// ./src/app/context/channel/agent/parser.js